import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

export const PROSE_MAX = '60ch';

export const SAVE_BUTTON_MIN_WIDTH = 160;

export const proseSx: SystemStyleObject<Theme> = { maxWidth: PROSE_MAX };

export const SHELL_GUTTER = { xs: 4, md: 7 } as const;

export const SHELL_BLOCK = { start: { xs: 4, md: 3 }, end: { xs: 4, md: 3 } } as const;

export const SHELL_VARS = {
  inlineStart: '--shell-inline-start',
  inlineEnd: '--shell-inline-end',
  blockEnd: '--shell-block-end',
} as const;

export type ShellVar = keyof typeof SHELL_VARS;

export const shellVar = (name: ShellVar): string => `var(${SHELL_VARS[name]}, 0px)`;

export interface ShellVarsOptions {
  readonly documentScrolls?: boolean;
}

const shellVarsAt = (theme: Theme, size: 'xs' | 'md', documentScrolls: boolean): SystemStyleObject<Theme> => ({
  [SHELL_VARS.inlineStart]: `max(env(safe-area-inset-left), ${theme.spacing(SHELL_GUTTER[size])})`,
  [SHELL_VARS.inlineEnd]: `max(env(safe-area-inset-right), ${theme.spacing(SHELL_GUTTER[size])})`,
  [SHELL_VARS.blockEnd]: documentScrolls ? '0px' : theme.spacing(SHELL_BLOCK.end[size]),
});

export const shellVarsSx = (theme: Theme, { documentScrolls = false }: ShellVarsOptions = {}): SystemStyleObject<Theme> => ({
  ...shellVarsAt(theme, 'xs', documentScrolls),
  [theme.breakpoints.up('md')]: shellVarsAt(theme, 'md', documentScrolls),
});

export const fillColumnSx: SystemStyleObject<Theme> = {
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  minBlockSize: 0,
  minInlineSize: 0,
};

export type ScreenWidth = 'narrow' | 'reading' | 'wide' | 'full';

export const SCREEN_WIDTH: Readonly<Record<ScreenWidth, (theme: Theme) => number | 'none'>> = {
  narrow: (theme) => theme.breakpoints.values.sm,
  reading: (theme) => theme.breakpoints.values.extraSm,
  wide: (theme) => theme.breakpoints.values.lg,
  full: () => 'none',
};

export const DOCK_SCROLL_CLEARANCE = (theme: Theme): string =>
  `calc(${theme.controlHeight.medium * 3}px + ${theme.spacing(6)} + env(safe-area-inset-bottom))`;

export const DOCK_ATTRIBUTE = 'data-action-dock';

export const screenRootSx =
  (width: ScreenWidth, endsWithDock: boolean) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    boxSizing: 'border-box',
    '&, & *, & *::before, & *::after': { boxSizing: 'border-box' },
    isolation: 'isolate',
    ...fillColumnSx,
    inlineSize: '100%',
    gap: 4,
    marginInlineEnd: 'auto',
    paddingBlockEnd: endsWithDock ? 0 : 6,
    [theme.breakpoints.up('md')]: { maxInlineSize: SCREEN_WIDTH[width](theme) },
  });

export const emptyRegionSx: SxProps<Theme> = {
  '&:empty': { position: 'absolute', width: 0, height: 0, overflow: 'hidden' },
};
